import React from 'react'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Typography from '@mui/material/Typography'
import { PieChart, Pie, Cell, Tooltip } from 'recharts'
import { useMediaQuery, Grid, Box } from '@mui/material'
import { theme } from '../../../theme/theme'
import { Circle } from '@mui/icons-material'

interface ChartDataType{
  name: string; value: number, color: string, icon: string
}

interface PieChartCardProps {
  title: string
  chartData: ChartDataType[]
  bgcolor: string
}

const CustomTooltip = ({ active, payload }: any) => {
  if (active && payload && payload.length) {
    const { name, value, color } = payload[0].payload
    return (
      <Box
        sx={{
          textAlign: 'center',
          alignItems:'center',
          backgroundColor:"#fff",
          p:2,
          display:'flex'
        }}
      >
        <Circle fontSize='small' sx={{color:`${color} !important`}}></Circle>
        <Typography variant="h5" color={'gray'} mx={3}>{name}</Typography>
        <Typography variant="h4" fontWeight={600}>{value}</Typography>
      </Box>
    )
  }

  return null
}

const CustomEmptyPieChart: React.FC<{
  color: string
}> = ({ color }) => (
  <PieChart width={260} height={230}>
    <Pie data={[{ name: 'Empty', value: 100 }]} outerRadius={115} dataKey="value">
      <Cell style={{outline: 'none'}} key={`cell-0`} fill={color} />
    </Pie>
  </PieChart>
)

const CustomPieChart: React.FC<{
  data: { name: string; value: number; color: string }[]
}> = ({ data }) => (
  <PieChart width={260} height={230}>
    <Tooltip content={<CustomTooltip />} />
    <Pie data={data} outerRadius={115} dataKey="value">
      {data.map((entry, index) => (
        <Cell style={{outline: 'none'}} key={`cell-${index}`} fill={entry.color} />
      ))}
    </Pie>
  </PieChart>
)

const PieChartCard: React.FC<PieChartCardProps> = ({
  title,
  chartData,
  bgcolor
}) => {
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const areDataValuesZero = chartData.every(item=>{
    return item.value ===0
  })

  return (
    <Card sx={{ borderRadius: 5 }}>
      <CardContent>
        <Typography variant="h3" sx={{ mb: 5 }}>
          {title}
        </Typography>
        <Grid container spacing={3} alignItems="center">
          
          <Grid
            item
            xs={12}
            md={12}
            lg={6}
            sx={{ display: 'flex', justifyContent: 'center'}}>
            <Box sx={{ ml: isMobile ? 5 : 0 }}>
              {areDataValuesZero? <CustomEmptyPieChart color={chartData[0].color}/> : <CustomPieChart data={chartData}/>}
            </Box>
          </Grid>

          <Grid item lg={6} xs={12} md={12} height={'100%'}>
            {chartData.map((data: ChartDataType, index)=>(
              <Box
                display="flex"
                alignItems="center"
                mb={2}
                justifyContent="space-between"
                p={2}
                bgcolor={bgcolor}
                borderRadius={2}>
              <Box display="flex" alignItems="center">
                <Box
                  p={2}
                  bgcolor={data.color}
                  borderRadius={2}
                  alignItems="center"
                  mr={1}>
                  <img
                    src={data.icon}
                    alt="icon"
                    width={18}
                    height={18}
                    style={{ display: 'block', margin: 'auto' }}
                  />
                </Box>
                <Typography variant="h4" ml={2}>
                  {data.name}
                </Typography>
              </Box>
              
              <Typography variant="h2">{data.value}</Typography>
            </Box>))}
           
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  )
}

export default PieChartCard
